import React from 'react';
import { Helmet } from 'react-helmet';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import ContactCTA from '../components/ContactCTA';

const GuideSection = ({ number, title, children }) => (
  <Reveal as="section" className="guide-section">
    <header>
      <span>{number}</span>
      <h2>{title}</h2>
    </header>
    <div className="guide-section__body">{children}</div>
  </Reveal>
);

const FAQPlus = () => (
  <>
    <Helmet>
      <title>偽物ウイスキーの見分け方 | RYU株式会社</title>
      <meta
        name="description"
        content="山崎・白州・響などの偽物ウイスキーを避けるための購入元、ボトル、外箱、価格、ホログラムシールの確認ポイントを正規酒類専門商社が解説します。"
      />
      <meta property="og:title" content="偽物ウイスキーの見分け方 | RYU株式会社" />
      <meta property="og:description" content="プロの経験に基づく、偽物ウイスキーを避けるための確認ポイント。" />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://www.ryukk.jp/faqplus" />
      <meta property="og:image" content="https://www.ryukk.jp/ogp.jpg" />
      <link rel="canonical" href="https://www.ryukk.jp/faqplus" />
    </Helmet>

    <PageHero
      label="AUTHENTICITY GUIDE"
      title="偽物ウイスキーの見分け方"
      description="正規流通に携わるRYU株式会社が、購入前に知っておきたい確認ポイントをご案内します。"
    />

    <article className="guide-page section">
      <div className="container guide-page__layout">
        <aside className="guide-page__aside">
          <p className="eyebrow">PROFESSIONAL VIEW</p>
          <p>最も大切なのは、外観だけに頼らず、流通経路が明確な販売元を選ぶことです。</p>
          <nav aria-label="記事内目次">
            <a href="#guide-01">購入元</a>
            <a href="#guide-02">ボトル</a>
            <a href="#guide-03">気泡</a>
            <a href="#guide-04">外箱</a>
            <a href="#guide-05">価格</a>
            <a href="#guide-06">シール</a>
            <a href="#guide-07">注意点</a>
          </nav>
        </aside>

        <div className="guide-page__content">
          <Reveal as="p" className="guide-page__lead">
            現在、日本国内外において特に「山崎」「白州」「響」などの人気ジャパニーズウイスキーを模倣した偽物商品の流通が報告されています。
            弊社では、長年にわたり正規流通ルートのみを取り扱ってきた専門商社として、以下のポイントを基に注意喚起を行っております。
          </Reveal>

          <div id="guide-01">
            <GuideSection number="01" title="購入元の確認（最重要）">
              <p>
                偽物を避けるうえで最も確実なのは、<strong>信頼できる販売元から購入すること</strong>です。
                フリマアプリ、個人出品、SNS取引などでは流通経路が不明確であり、偽物のリスクが極めて高い傾向があります。
              </p>
              <p>
                実店舗や公式ECサイト、正規販売契約を結んだ酒類販売店など、<strong>流通ルートが明確な店舗</strong>を選ぶことが推奨されます。
              </p>
            </GuideSection>
          </div>

          <div id="guide-02">
            <GuideSection number="02" title="ボトルの形状と偽造リスク">
              <p>
                正規ウイスキーのボトルは、ブランドごとに独自設計が施されており、瓶の形状・重量・ガラスの厚み・注ぎ口の構造などに一貫性があります。
                一方で、年による製造ロット差やリニューアルもあるため、わずかな外観の違いだけで真贋を判断するのは危険です。
              </p>
              <p>
                また、現在市場では<strong>空き瓶や箱を悪用した“再充填型の偽物”</strong>にも注意が必要です。
                フリマアプリやネットオークションなどで空瓶と外箱が高値で流通し、それらを使って中身の異なるウイスキーを本物として販売するケースが報告されています。
              </p>
              <p>
                このような再利用手口は、ラベルやキャップが本物であるため外観上の見分けがほぼ不可能です。
                そのため、見た目の判断よりも<strong>購入元の信頼性</strong>を第一にすることを強くおすすめします。
              </p>
            </GuideSection>
          </div>

          <div id="guide-03">
            <GuideSection number="03" title="液体を振って気泡で判断できる？">
              <p>
                一部の情報では「ウイスキーを振って泡の残り方で本物か偽物かが判断できる」と言われていますが、
                <strong>この方法は極めて限定的かつ専門性の高い判断手法</strong>です。
              </p>
              <p>
                実際には、ウイスキーのアルコール度数、熟成年数、原料構成、保管環境などが泡の残り方に影響を与えるため、
                泡だけを根拠に正否を断定することはできません。経験豊富な鑑定士や研究者が複数のサンプルと比較しながら行う補助的な手段であり、
                一般消費者が一瓶のみで判断することは誤解を生む可能性が高いと考えられます。
              </p>
            </GuideSection>
          </div>

          <div id="guide-04">
            <GuideSection number="04" title="外箱・包装の質感と構造">
              <p>ウイスキーの偽物は、パッケージ（箱・包装）から粗が出るケースが多くあります。</p>
              <div className="guide-comparison">
                <div>
                  <span>AUTHENTIC</span>
                  <h3>正規品の傾向</h3>
                  <ul>
                    <li>厚みのある紙質で、手触りが滑らか</li>
                    <li>ブランドロゴや金箔などの印刷がシャープで位置も正確</li>
                  </ul>
                </div>
                <div>
                  <span>CAUTION</span>
                  <h3>注意すべき傾向</h3>
                  <ul>
                    <li>光沢が強すぎる・マットすぎる、または明らかに紙質が薄い</li>
                    <li>印刷ズレ・色の違いがある</li>
                    <li>商品を入れると箱が歪む、すぐ破れる</li>
                  </ul>
                </div>
              </div>
              <p>とくに響 JAPANESE HARMONYや山崎12年の箱は偽物も多いため、外箱の細部まで確認することが重要です。</p>
            </GuideSection>
          </div>

          <div id="guide-05">
            <GuideSection number="05" title="価格が安すぎる場合は要警戒">
              <p>
                信頼できるショップを装って、明らかに市場相場より安すぎる価格で販売されている商品には注意が必要です。
                例えば山崎12年が1万円台で販売されていれば極めて不自然です。
              </p>
              <p>
                正規品の価格は流通経路が限られているため、極端な値引きは考えにくいものです。
                特にフリマアプリ・SNS広告などでは、購入者心理を逆手に取った巧妙な偽装販売が多く報告されています。
              </p>
            </GuideSection>
          </div>

          <div id="guide-06">
            <GuideSection number="06" title="サントリーのホログラムシール制度">
              <p className="guide-section__note">2024年3月製造分から順次導入</p>
              <p>
                以下はサントリー株式会社の公式発表（2024年2月29日）に基づいています。
                一部国産プレミアムウイスキーに複製・再貼付が難しいホログラムシール2種類を貼付し、市場に流通する商品のブランドを守る取り組みが強化されました。
              </p>
              <ul>
                <li>特長：複製や再貼付が難しい特殊加工</li>
                <li>貼付箇所：開封口・ボトル背面の計2箇所</li>
                <li>「響」30年／21年／BLENDER’S CHOICE／JAPANESE HARMONY</li>
                <li>「山崎」25年／18年／12年</li>
                <li>「白州」25年／18年／12年</li>
              </ul>
              <p>※順次切り替え出荷のため、市場にはホログラムシールが2種類貼付されていない商品も流通しています。</p>
              <div className="guide-images">
                <figure>
                  <img src="/hologram-seal.jpg" alt="開封口に貼付されるホログラムシール" loading="lazy" />
                </figure>
                <figure>
                  <img src="/hologram-seal2.jpg" alt="ボトル背面に貼付されるホログラムシール" loading="lazy" />
                </figure>
              </div>
            </GuideSection>
          </div>

          <div id="guide-07">
            <GuideSection number="07" title="ご購入前の注意点">
              <p>
                少しでも不安や違和感を覚えた場合は、開封せず、まず販売元へ確認することをおすすめします。
                見た目では判断が難しいケースも多いため、信頼できる販売経路での購入が最も安全です。
                また、購入前に販売業者の評価や過去のレビューを確認するのも有効な手段です。
              </p>
            </GuideSection>
          </div>

          <Reveal className="guide-page__closing">
            <p>
              このページは正規販売事業者であるRYU株式会社が、過去の事例やメーカー情報に基づいて作成しています。
              安心・安全な酒類流通のため、引き続きご理解とご協力をお願いいたします。
            </p>
          </Reveal>
        </div>
      </div>
    </article>

    <ContactCTA />
  </>
);

export default FAQPlus;
